import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Radio, Skeleton } from '@mui/material';
import type {
  LocationItem,
  SmartRoutingSearchEntity,
  SmartRoutingEntityType,
  SmartRoutingV2,
  SmartRoutingStartingLocation,
} from '@trinity/chat';
import { SearchQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint';
import { SelectLocation } from '@trinity/chat-components/components/Search/Location';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useBrowserLocation } from '@trinity/chat-components/hooks/useBrowserLocation';
import { BrowserLocationError } from '../Chat/BrowserLocationError';
import { SelectEntities } from '../Chat/SelectEntities';
import {
  getSmartRoutingEntitySearchPlaceholder,
  getSmartRoutingEntitySingularLabelWithArticle,
} from './entity-copy.js';
import type { SmartRoutingAction } from './SmartRouting.Container';

type Props = {
  value: SmartRoutingV2;
  disabled: boolean;
  validEntities: SmartRoutingEntityType[];
  fixedEntity: SmartRoutingSearchEntity | null;
  isFixedEntityLoading?: boolean;
  dispatch: React.Dispatch<SmartRoutingAction>;
  onLocationSubmitted?: (startingLocation: SmartRoutingStartingLocation | null) => Promise<void>;
};

function getEntityLocationData(selectedEntity: SmartRoutingSearchEntity) {
  if (!selectedEntity.locationCoords) {
    return null;
  }

  return {
    id: selectedEntity.entityId.toString(),
    type: selectedEntity.entityType,
    latitude: selectedEntity.locationCoords.latitude,
    longitude: selectedEntity.locationCoords.longitude,
  };
}

function buildSmartRoutingLocation(
  radioState: 'browser' | 'search' | 'entity',
  browserLocation: ICoords | null,
  selectorState: LocationItem | null,
  selectedEntity: SmartRoutingSearchEntity | null
): SmartRoutingStartingLocation | null {
  if (radioState === 'browser' && browserLocation) {
    return {
      type: 'browser',
      latitude: browserLocation.latitude,
      longitude: browserLocation.longitude,
    };
  }
  if (radioState === 'search' && selectorState) {
    return {
      type: 'search',
      name: selectorState.name,
      latitude: selectorState.latitude,
      longitude: selectorState.longitude,
      id: selectorState.id,
    };
  }
  if (radioState === 'entity' && selectedEntity) {
    const entityLocation = getEntityLocationData(selectedEntity);
    if (!entityLocation) {
      return null;
    }

    return {
      type: 'entity',
      entityId: entityLocation.id,
      entityType: entityLocation.type,
      entityName: selectedEntity.name,
      latitude: entityLocation.latitude,
      longitude: entityLocation.longitude,
    };
  }
  return null;
}

function computeSmartRoutingCanSubmit(
  disabled: boolean,
  radioState: string,
  browserLocation: ICoords | null,
  selectorState: LocationItem | null,
  selectedEntity: SmartRoutingSearchEntity | null
) {
  if (disabled) return false;
  if (radioState === 'browser' && browserLocation) return true;
  if (radioState === 'search' && selectorState) return true;
  if (radioState === 'entity' && selectedEntity) return true;
  return false;
}

function getInitialRadioState(
  entityFromContext: SmartRoutingSearchEntity | null,
  value: SmartRoutingV2
): 'browser' | 'search' | 'entity' {
  if (entityFromContext) return 'entity';
  const type = value?.startingLocation?.type;
  if (type === 'hcp' || type === 'entity') return 'entity';
  if (type === 'search') return 'search';
  return 'browser';
}

function getInitialSelectorState(value: SmartRoutingV2): LocationItem | null {
  return value?.startingLocation?.type === 'search' ? value.startingLocation : null;
}

function getInitialBrowserLocation(value: SmartRoutingV2): ICoords | null {
  return value?.startingLocation?.type === 'browser' ? value.startingLocation : null;
}

function SmartRoutingBrowserStatus({
  state,
  browserLocation,
  locationError,
}: {
  state: string;
  browserLocation: ICoords | null;
  locationError: string | null;
}) {
  if (state === 'loading') return <div>Waiting for permission...</div>;
  if (state === 'success' && browserLocation)
    return (
      <div>
        {browserLocation.longitude}, {browserLocation.latitude}
      </div>
    );
  if (state === 'error') {
    return <BrowserLocationError errorMessage={locationError ?? 'Unable to determine your location.'} />;
  }
  return null;
}

function EntitySearchSelector({
  disabled,
  selectedEntity,
  entityInputValue,
  handleEntityInputFocus,
  handleEntityInputBlur,
  setEntityInputValue,
  handleEntitySelection,
  queryFn,
  validEntities,
}: {
  disabled: boolean;
  selectedEntity: SmartRoutingSearchEntity | null;
  entityInputValue: string;
  handleEntityInputFocus: () => void;
  handleEntityInputBlur: () => void;
  setEntityInputValue: (v: string) => void;
  handleEntitySelection: (v: SmartRoutingSearchEntity | null) => void;
  queryFn: (params: {
    name: string;
    brandIdentifier: string;
    territoryId?: string;
    validEntities: SmartRoutingEntityType[];
  }) => Promise<{ results: SmartRoutingSearchEntity[] }>;
  validEntities: SmartRoutingEntityType[];
}) {
  const placeholder = useMemo(() => getSmartRoutingEntitySearchPlaceholder(validEntities), [validEntities]);

  return (
    <SelectEntities
      disabled={disabled}
      placeholder={placeholder}
      formatOptionLabel={formatEntityOptionLabel}
      onInputFocus={handleEntityInputFocus}
      onInputBlur={handleEntityInputBlur}
      onInputChange={setEntityInputValue}
      onSelection={handleEntitySelection}
      isClearable={true}
      value={selectedEntity}
      inputValue={selectedEntity ? undefined : entityInputValue}
      validEntities={validEntities}
      queryFn={queryFn}
    />
  );
}

export const SmartRoutingLocation = (props: Props) => {
  const [radioState, setRadioState] = useState<'browser' | 'search' | 'entity'>(
    getInitialRadioState(props.fixedEntity, props.value)
  );
  const [selectorState, setSelectorState] = useState<LocationItem | null>(
    getInitialSelectorState(props.value)
  );
  const [browserLocation, setBrowserLocation] = useState<ICoords | null>(
    getInitialBrowserLocation(props.value)
  );
  const [selectedEntity, setSelectedEntity] = useState<SmartRoutingSearchEntity | null>(props.fixedEntity);
  const [entityInputValue, setEntityInputValue] = useState<string>('');
  const [locationError, setLocationError] = useState<string | null>(null);
  const {
    isSupported,
    getLocation,
    state: browserLocationState,
  } = useBrowserLocation({ onSuccess: setBrowserLocation, onError: setLocationError });

  useEffect(() => {
    if (radioState === 'browser' && !browserLocation) {
      // Errors are already surfaced via the hook's onError callback (see locationError below);
      // the rejection itself is intentionally not awaited here.
      getLocation().catch(() => {});
    }
  }, [browserLocation, getLocation, radioState]);

  // fixedEntity is resolved asynchronously (see isFixedEntityLoading above), so it's typically
  // still null on first render and only arrives after this component has already mounted with
  // the useState initializers above. Seed selectedEntity/radioState once it resolves, guarded so
  // we only do this the first time (not on every render, and not if the user has since changed
  // their selection).
  const hasSeededFixedEntityRef = useRef(false);
  useEffect(() => {
    if (!props.fixedEntity || hasSeededFixedEntityRef.current) return;
    hasSeededFixedEntityRef.current = true;
    setSelectedEntity(props.fixedEntity);
    setRadioState('entity');
  }, [props.fixedEntity]);

  const onSubmit = useCallback(async () => {
    const startingLocation = buildSmartRoutingLocation(
      radioState,
      browserLocation,
      selectorState,
      selectedEntity
    );

    if (startingLocation) {
      props.dispatch({
        type: 'submit-location',
        payload: { startingLocation },
      });
      if (props.onLocationSubmitted) {
        await props.onLocationSubmitted(startingLocation);
      }
    }
  }, [browserLocation, props, radioState, selectedEntity, selectorState]);

  const inputFocused = useRef(false);
  const handleEntityInputBlur = useCallback(() => {
    inputFocused.current = false;
  }, []);

  const handleEntityInputFocus = useCallback(() => {
    inputFocused.current = true;
  }, []);

  const handleEntitySelection = useCallback((v: SmartRoutingSearchEntity | null) => {
    setSelectedEntity(v);
    if (!v) setEntityInputValue('');
  }, []);

  const canSubmit = useMemo(
    () =>
      computeSmartRoutingCanSubmit(
        props.disabled,
        radioState,
        browserLocation,
        selectorState,
        selectedEntity
      ),
    [browserLocation, props.disabled, radioState, selectedEntity, selectorState]
  );

  const apiClient = useChatApiClient();
  const entityLocationLabel = useMemo(() => {
    return getSmartRoutingEntitySingularLabelWithArticle(props.validEntities);
  }, [props.validEntities]);

  const queryEntitiesByName = useCallback(
    ({
      name,
      brandIdentifier,
      territoryId,
      validEntities,
    }: {
      name: string;
      brandIdentifier: string;
      territoryId?: string;
      validEntities: SmartRoutingEntityType[];
    }) =>
      apiClient.smartRouting.searchEntities({
        name,
        brandIdentifier,
        territoryId,
        validEntities,
      }),
    [apiClient]
  );

  if (props.isFixedEntityLoading) {
    return (
      <List>
        <Skeleton variant="text" width="95%" height={22} />
        <Skeleton variant="text" width="80%" height={22} />
        <Skeleton variant="text" width="65%" height={22} />
      </List>
    );
  }

  return (
    <div>
      <Copy>Welcome to the Smart Routing Assistant! I can help you plan your site visits efficiently.</Copy>
      <Copy>First let's plan our where you'll be starting from.</Copy>
      <List>
        <RadioRow>
          <Radio
            size="small"
            disabled={!isSupported || props.disabled}
            checked={radioState === 'browser'}
            onClick={() => setRadioState('browser')}
          />
          <div>Use my current location</div>
          <SmartRoutingBrowserStatus
            state={browserLocationState}
            browserLocation={browserLocation}
            locationError={locationError}
          />
        </RadioRow>
        <RadioRow>
          <Radio
            size="small"
            checked={radioState === 'search'}
            disabled={props.disabled}
            onClick={() => setRadioState('search')}
          />
          <RadioCopy>I'll be near this location</RadioCopy>
          <SelectLocation
            styles={{
              container: (provided) => ({
                ...provided,
                width: 350,
              }),
            }}
            placeholder="Start typing an address"
            isDisabled={radioState !== 'search' || props.disabled}
            value={selectorState ?? { id: '', name: '', latitude: 0, longitude: 0 }}
            onChange={(value) => setSelectorState(value)}
          />
        </RadioRow>
        <RadioRow>
          <Radio
            size="small"
            checked={radioState === 'entity'}
            disabled={props.disabled}
            onClick={() => setRadioState('entity')}
          />

          <RadioCopy>Use {entityLocationLabel} location</RadioCopy>
          <EntitySelectContainer>
            <EntitySearchSelector
              disabled={props.disabled || radioState !== 'entity' || !!props.fixedEntity}
              selectedEntity={selectedEntity}
              entityInputValue={entityInputValue}
              handleEntityInputFocus={handleEntityInputFocus}
              handleEntityInputBlur={handleEntityInputBlur}
              setEntityInputValue={setEntityInputValue}
              handleEntitySelection={handleEntitySelection}
              queryFn={queryEntitiesByName}
              validEntities={props.validEntities}
            />
          </EntitySelectContainer>
        </RadioRow>
      </List>
      <StyledSearchEntities disabled={!canSubmit} onClick={onSubmit} value="Search Nearby" />
    </div>
  );
};

function formatEntityOptionLabel(result: SmartRoutingSearchEntity) {
  if (result.entityType === 'account') {
    return (
      <div>
        {result.name}
        {result.category ? <KolOptionSubText>({result.category})</KolOptionSubText> : null}
      </div>
    );
  }

  const specialty = result.specialty?.join(', ');
  const hasValidAffiliation = Boolean(result.primaryAffiliationName) && result.primaryAffiliationName !== 'N';
  const affiliation = hasValidAffiliation ? result.primaryAffiliationName?.trim() : null;

  return (
    <div>
      {result.name}
      {specialty ? <KolOptionSubText>({specialty})</KolOptionSubText> : null}
      {affiliation ? <KolResultSecondaryText>{affiliation}</KolResultSecondaryText> : null}
    </div>
  );
}

const KolResultSecondaryText = styled.div(({ theme }) => ({
  color: theme.palette.gray.main,
  marginTop: 5,
  fontSize: 15,
}));
const KolOptionSubText = styled.span(({ theme }) => ({
  fontSize: 14,
  marginLeft: 5,
  color: theme.palette.gray.main,
}));

const RadioCopy = styled.div({
  width: 165,
});

const Copy = styled.div({
  marginBottom: 5,
});

const StyledSearchEntities = styled(SearchQueryHint)({
  marginTop: 10,
});

const List = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

const RadioRow = styled.div(() => ({
  display: 'flex',
  gap: 10,
  alignItems: 'center',
}));

const EntitySelectContainer = styled.div({
  width: 350,
});
